import React, { useMemo, useState } from "react";
import { Part } from "../../types/parts";
import { ViewerSettings } from "../../types/settings";
import { t } from "../../utils/i18n";
import { withOpacity } from "../../utils/colorUtils";
interface PartsPanelProps {
  isOpen?: boolean;
  parts?: Part[];
  selectedPartId?: string;
  onPartSelect?: (part: Part) => void;
  onViewItem?: (part: Part, itemType: "part") => void;
  panelTitle?: string;
  backgroundColor?: string;
  backdropBlur?: string;
  borderColor?: string;
  boxShadow?: string;
  titleColor?: string;
  textColor?: string;
  mutedTextColor?: string;
  iconColor?: string;
  activeColor?: string;
  fontFamily?: string;
  language?: ViewerSettings["language"];
}

const DEFAULT_PARTS: Part[] = [
  {
    id: "arm-frame-left",
    name: "Arm Frame Left",
    code: "AF-L-001",
    icon: "view_in_ar",
  },
  {
    id: "arm-frame-right",
    name: "Arm Frame Right",
    code: "AF-R-002",
    icon: "view_in_ar",
  },
  {
    id: "motor-housing",
    name: "Motor Housing",
    code: "MH-120-A",
    icon: "precision_manufacturing",
  },
  {
    id: "propeller-set",
    name: "Propeller Set",
    code: "PS-4X-09",
    icon: "cycle",
  },
  {
    id: "flight-controller",
    name: "Flight Controller",
    code: "FC-PRO-7",
    icon: "memory",
  },
  {
    id: "power-distribution",
    name: "Power Distribution Board",
    code: "PDB-48V",
    icon: "electric_bolt",
  },
  {
    id: "gps-module",
    name: "GPS Module",
    code: "GPS-M8N",
    icon: "my_location",
  },
  {
    id: "battery-pack",
    name: "Battery Pack",
    code: "BP-6S-5000",
    icon: "battery_full",
  },
];

export function PartsPanel({
  isOpen = false,
  parts = DEFAULT_PARTS,
  selectedPartId,
  onPartSelect,
  onViewItem,
  panelTitle = "Parts",
  backgroundColor = "rgba(55, 55, 55, 0.85)",
  backdropBlur = "24px",
  borderColor = "rgba(255, 255, 255, 0.12)",
  boxShadow = "0 8px 32px 0 rgba(0, 0, 0, 0.4)",
  titleColor = "rgb(56, 189, 248)",
  textColor = "rgba(255, 255, 255, 0.9)",
  mutedTextColor = "rgba(255, 255, 255, 0.45)",
  iconColor = "rgba(255, 255, 255, 0.75)",
  activeColor = "rgb(56, 189, 248)",
  fontFamily = "Arial, sans-serif",
  language = "en",
}: PartsPanelProps) {
  const [query, setQuery] = useState("");
  const [localSelectedPartId, setLocalSelectedPartId] = useState<
    string | undefined
  >(parts[0]?.id);

  const activePartId = selectedPartId ?? localSelectedPartId;

  const filteredParts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return parts;

    return parts.filter(
      (part) =>
        part.name.toLowerCase().includes(normalizedQuery) ||
        part.code.toLowerCase().includes(normalizedQuery),
    );
  }, [parts, query]);

  if (!isOpen) return null;

  return (
    <aside
      style={{
        position: "absolute",
        left: "1.5rem",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 100,
        width: "18rem",
        height: "calc(100% - 12rem)",
        maxHeight: "700px",
      }}
      data-oid="rgbdmwp"
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          backgroundColor,
          backdropFilter: `blur(${backdropBlur})`,
          WebkitBackdropFilter: `blur(${backdropBlur})`,
          border: `1px solid ${borderColor}`,
          boxShadow,
          borderRadius: "1.5rem",
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          fontFamily,
        }}
        data-oid=".6l7_.v"
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          data-oid="2x7cn0y"
        >
          <h2
            style={{
              margin: 0,
              fontSize: "0.875rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              color: `${titleColor}CC`,
            }}
            data-oid="glxx_uw"
          >
            {panelTitle === "Parts" ? t(language, "panel.parts") : panelTitle}
          </h2>
          <span
            style={{
              fontSize: "0.625rem",
              padding: "0.25rem 0.5rem",
              borderRadius: "0.25rem",
              color: mutedTextColor,
              backgroundColor: "rgba(255, 255, 255, 0.05)",
            }}
            data-oid="rg15:iu"
          >
            {filteredParts.length} {t(language, "panel.items")}
          </span>
        </div>

        <div style={{ position: "relative" }} data-oid="8.cocf6">
          <span
            className="material-symbols-outlined"
            style={{
              position: "absolute",
              left: "0.75rem",
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "0.875rem",
              color: mutedTextColor,
              fontWeight: 300,
              pointerEvents: "none",
            }}
            data-oid="-7oemx8"
          >
            search
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t(language, "panel.searchParts")}
            style={{
              width: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: "0.75rem",
              padding: "0.5rem 1rem 0.5rem 2.25rem",
              fontSize: "0.75rem",
              color: textColor,
              outline: "none",
              transition: "all 0.2s ease",
            }}
            onFocus={(e) => {
              e.currentTarget.style.border =
                `1px solid ${withOpacity(activeColor, 0.5)}`;
              e.currentTarget.style.boxShadow =
                `0 0 0 1px ${withOpacity(activeColor, 0.2)}`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.border =
                "1px solid rgba(255, 255, 255, 0.1)";
              e.currentTarget.style.boxShadow = "none";
            }}
            data-oid="4t1amtp"
          />
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            paddingRight: "0.5rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.25rem",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(255, 255, 255, 0.2) transparent",
          }}
          data-oid="wqe1u13"
        >
          {filteredParts.map((part) => {
            const isActive = part.id === activePartId;
            return (
              <button
                key={part.id}
                onClick={() => {
                  setLocalSelectedPartId(part.id);
                  onPartSelect?.(part);
                }}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "0.75rem",
                  padding: "0.75rem",
                  borderRadius: "0.75rem",
                  cursor: "pointer",
                  border: isActive
                    ? `1px solid ${activeColor}4D`
                    : "1px solid transparent",
                  background: isActive ? `${activeColor}1A` : "transparent",
                  color: isActive ? activeColor : textColor,
                  transition: "all 0.2s",
                  textAlign: "left",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.05)";
                    e.currentTarget.style.border =
                      "1px solid rgba(255, 255, 255, 0.1)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.border = "1px solid transparent";
                  }
                }}
                data-oid="fylxlep"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    flex: 1,
                    minWidth: 0,
                  }}
                  data-oid="wo.n3vz"
                >
                  <span
                    className="material-symbols-outlined"
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: 300,
                      color: isActive ? activeColor : iconColor,
                      flexShrink: 0,
                    }}
                    data-oid="hvmxlsh"
                  >
                    {part.icon}
                  </span>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      minWidth: 0,
                    }}
                    data-oid="5kxpet:"
                  >
                    <span
                      style={{
                        fontSize: "0.8125rem",
                        fontWeight: 500,
                        color: isActive ? activeColor : textColor,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      data-oid=":ne2-0n"
                    >
                      {part.name}
                    </span>
                    <span
                      style={{
                        fontSize: "0.625rem",
                        color: isActive ? `${activeColor}99` : mutedTextColor,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      data-oid="-9rt1j_"
                    >
                      {part.code}
                    </span>
                  </div>
                </div>

                {/* View Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewItem?.(part, "part");
                  }}
                  className="viewButton"
                  style={{
                    width: "32px",
                    height: "32px",
                    background: "transparent",
                    color: mutedTextColor,
                    border: "none",
                    borderRadius: "0.5rem",
                    cursor: "pointer",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      withOpacity(activeColor, 0.15);
                    e.currentTarget.style.color = activeColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = mutedTextColor;
                  }}
                  aria-label={`${t(language, "panel.viewDetailsFor")} ${part.name}`}
                  data-oid="z6j575v"
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: "1.25rem", fontWeight: 300 }}
                    data-oid="hrscrsh"
                  >
                    visibility
                  </span>
                </button>
              </button>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
